import { useEffect, useState } from "react";
import { api } from "../lib/api";
import HeroSection from "../components/HeroSection";
import Carousel from "../components/Carousel";
import CategoryTabs from "../components/CategoryTabs";
import SeriesCard from "../components/SeriesCard";
import VideoCard from "../components/VideoCard";
import ContinueWatchingCard from "../components/ContinueWatchingCard";
import SkeletonRow from "../components/SkeletonRow";
import { getContinueWatching } from "../lib/localStore";
import { mediaUrl } from "../lib/media";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";

export default function Home() {
    const [series, setSeries] = useState([]);
    const [viral, setViral] = useState([]);
    const [ai, setAi] = useState([]);
    const [comingSoon, setComingSoon] = useState([]);
    const [continueList, setContinueList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viralCat, setViralCat] = useState("All");
    const [aiCat, setAiCat] = useState("All");

    useEffect(() => {
        (async () => {
            try {
                const [s, v, a, c] = await Promise.all([
                    api.get("/series"),
                    api.get("/videos/viral"),
                    api.get("/ai-creative"),
                    api.get("/coming-soon"),
                ]);
                setSeries(s.data);
                setViral(v.data);
                setAi(a.data);
                setComingSoon(c.data);
            } finally {
                setLoading(false);
            }
        })();
        setContinueList(getContinueWatching());
    }, []);

    const hero = series.find(s => s.id === "blacklist") || series[0];
    const trending = viral.filter(v => v.trending);
    const viralFiltered = viralCat === "All" ? viral : viral.filter(v => (v.category || "") === viralCat);
    const aiFiltered = aiCat === "All" ? ai : ai.filter(v => (v.category || "") === aiCat);

    return (
        <div data-testid="home-page">
            <HeroSection series={hero} />

            {/* Continue Watching */}
            {continueList.length > 0 && (
                <div className="mt-12">
                    <Carousel title="Continue Watching" testId="continue-watching">
                        {continueList.map((e) => <ContinueWatchingCard key={e.id} entry={e} />)}
                    </Carousel>
                </div>
            )}

            {/* Web Series */}
            <div className="mt-16">
                {loading ? (
                    <SkeletonRow width="w-[260px]" aspect="aspect-[2/3]" />
                ) : (
                    <Carousel title="Web Series" subtitle="Premium AI originals" testId="web-series">
                        {series.map((s) => <SeriesCard key={s.id} series={s} />)}
                    </Carousel>
                )}
            </div>

            {/* Trending Now */}
            {trending.length > 0 && (
                <div className="mt-16">
                    <Carousel title="Trending Now" subtitle="Popular this week" testId="trending">
                        {trending.map((v) => <VideoCard key={v.id} video={v} />)}
                    </Carousel>
                </div>
            )}

            {/* Viral Videos */}
            <div className="mt-16">
                {loading ? <SkeletonRow /> : (
                    <Carousel
                        title="Viral Videos"
                        testId="viral"
                        filters={<CategoryTabs items={viral} active={viralCat} onChange={setViralCat} testId="home-viral-cats" />}
                    >
                        {viralFiltered.map((v) => <VideoCard key={v.id} video={v} />)}
                    </Carousel>
                )}
            </div>

            {/* AI Creative preview */}
            <div className="mt-16">
                {loading ? <SkeletonRow /> : (
                    <Carousel
                        title="AI Creative"
                        testId="ai-creative"
                        filters={<CategoryTabs items={ai} active={aiCat} onChange={setAiCat} testId="home-ai-cats" />}
                    >
                        {aiFiltered.slice(0, 12).map((v) => <VideoCard key={v.id} video={v} kind="ai" />)}
                    </Carousel>
                )}
            </div>

            {/* Featured Creator */}
            <section data-testid="featured-creator" className="mx-auto max-w-[1600px] px-6 lg:px-10 mt-24">
                <div className="relative overflow-hidden rounded-[24px] border border-white/10 bg-gradient-to-br from-[#161616] to-[#0B0B0B] p-8 sm:p-12">
                    <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[#FFD400]/10 blur-3xl" />
                    <div className="relative flex flex-col lg:flex-row items-start lg:items-center gap-8">
                        <div className="flex-1">
                            <div className="text-[11px] uppercase tracking-widest text-[#FFD400] font-bold mb-4 flex items-center gap-2">
                                <Sparkles className="w-4 h-4" /> Official Studio
                            </div>
                            <img
                                src="/apni-dukan-logo-transparent.png"
                                alt="Apni Dukan"
                                data-testid="creator-logo"
                                className="h-28 sm:h-36 w-auto object-contain object-left"
                            />
                            <p className="text-white/70 mt-4 max-w-2xl">India's AI Entertainment Platform for Web Series, Viral Videos &amp; Cinematic Storytelling.</p>
                            <div className="mt-6 flex flex-wrap gap-6 text-sm">
                                <div>
                                    <div className="font-display font-bold text-2xl text-white">3+</div>
                                    <div className="text-xs text-white/50 uppercase tracking-wider">Web Series</div>
                                </div>
                                <div>
                                    <div className="font-display font-bold text-2xl text-white">400+</div>
                                    <div className="text-xs text-white/50 uppercase tracking-wider">AI Projects</div>
                                </div>
                                <div>
                                    <div className="font-display font-bold text-2xl text-white">100K+</div>
                                    <div className="text-xs text-white/50 uppercase tracking-wider">Community</div>
                                </div>
                            </div>
                        </div>
                        <Link
                            to="/series"
                            data-testid="explore-content-btn"
                            className="inline-flex items-center gap-2 bg-[#FFD400] text-black font-bold rounded-full px-6 py-3 hover:scale-105 transition-transform"
                        >
                            <Sparkles className="w-4 h-4" /> Explore Content
                        </Link>
                    </div>
                </div>
            </section>

            {/* Coming Soon preview */}
            <div className="mt-24">
                <Carousel title="Coming Soon" subtitle="New drops on the horizon" testId="coming-soon-preview">
                    {comingSoon.map((c) => (
                        <Link
                            key={c.id}
                            to="/coming-soon"
                            data-testid={`coming-preview-${c.id}`}
                            className="group relative shrink-0 w-[280px] snap-start"
                        >
                            <div className="relative aspect-[2/3] rounded-[18px] overflow-hidden bg-white/5 border border-white/10">
                                <img src={mediaUrl(c.poster)} alt={c.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                                <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider bg-[#FFD400] text-black px-2 py-1 rounded">
                                    Coming Soon
                                </span>
                                <div className="absolute bottom-0 inset-x-0 p-4">
                                    <h3 className="font-display font-bold text-lg text-white">{c.title}</h3>
                                    <p className="text-xs text-white/60 mt-1 line-clamp-2">{c.subtitle}</p>
                                </div>
                            </div>
                        </Link>
                    ))}
                </Carousel>
            </div>
        </div>
    );
}
